<template>
  <div class="d-flex flex-column flex-grow-1">

    <!-- Suggested Questions Button & Panel -->
    <div class="suggested-questions-container mb-2 ml-4">
      <div class="d-flex justify-content-between">
        <button class="suggested-btn mb-2" @click="toggleSuggestedQuestions">
          <i class="fa fa-lightbulb-o me-1"></i>
          {{ showSuggestedQuestions ? $t('title.hideSuggestedQuestions') : $t('title.showSuggestedQuestions') }}
        </button>

        <div class="assistant-dropdown-container mb-2">
          <label class="dropdown-label">{{ $t('title.aiIndex') }}:</label>
          <select v-model="selectedAssistant" class="form-control assistant-dropdown">
            <option value="automatic">{{ $t('title.automatic') }}</option>
            <option value="rakoon_sap">{{ $t('title.rakoonSap') }}</option>
            <option value="rakoon_salesforse_monday">{{ $t('title.rakoonSalesforceMonday') }}</option>
            <option value="rakoon_office_gsuit">{{ $t('title.rakoonOfficeGsuite') }}</option>
            <option value="rakoon_notion">{{ $t('title.rakoonNotion') }}</option>
          </select>
        </div>
      </div>

      <div v-if="showSuggestedQuestions" class="suggested-questions-panel">
        <div class="question-categories">
          <button v-for="(category, catIndex) in questionCategories" :key="catIndex" class="category-tab"
            :class="{ active: activeCategory === catIndex }" @click="activeCategory = catIndex">
            {{ category.name }}
          </button>
        </div>

        <div class="question-list-container">
          <div class="question-list">
            <button v-for="(question, index) in filteredQuestions" :key="index" class="suggested-question-btn"
              @click="selectSuggestedQuestion(question)">
              {{ question }}
            </button>
          </div>
        </div>

        <div class="search-container">
          <input type="text" v-model="searchQuery" :placeholder="$t('title.searchQuestions')"
            class="form-control form-control-sm">
        </div>
      </div>
    </div>

    <div class="chat-container" ref="chatContainer">
      <ul class="chat-box chatContainerScroll" v-for="(chat, index) in chats" :key="index">
        <li class="chat-right" v-if="chat.question">
          <div class="chat-text">{{ chat.question }}</div>
        </li>
        <li class="chat-left" v-if="chat.response_text || chat.isStreaming">
          <h6>
            <span v-if="chat.index_name">
              <span v-for="(name, i) in chat.index_name.split(',')" :key="i">
                {{ indexNames[name.trim()] || name.trim() }}
                <span v-if="i < chat.index_name.split(',').length - 1">, </span>
              </span>
              {{ $t('title.index') }}:
            </span>
          </h6>

          <!-- Single-line status ticker above current assistant message -->
          <div v-if="isSubmit && index === chats.length - 1 && statusTicker" class="status-ticker mb-2">
            <i class="fa fa-robot"></i>
            <span class="text" :key="statusTicker">{{ statusTicker }}</span>
            <span class="typing-dots small"><span></span><span></span><span></span></span>
          </div>

          <!-- Response Text -->
          <div class="chat-text animated-reveal" :ref="'chatText-' + index" :class="{ 'streaming': chat.isStreaming }" v-html="chat.response_text">
            <div v-if="chat.isStreaming && !chat.response_text" class="typing-indicator">
              <div class="typing-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
          <!-- <div
            v-if="chat.index_name && chat.index_name.split(',').length > 1 && !chat.merged && index === chats.length - 1 && !chat.is_merged">
            <div class="merge-question mt-2 p-2">
              <p class="fw-bold text-primary">
                ⚡ I found relevant information in <strong>multiple sources</strong>.
                Would you like me to <strong>merge and refine the above answers</strong>?
              </p>
              <button class="btn btn-sm btn-success me-2" @click="mergeAnswers(chat)">✅ Yes</button>
            </div>
          </div> -->
        </li>
      </ul>
      
    </div>

    <div class="form-group mt-1 mb-1 d-flex flex-column align-items-end chat-form-message">
      <textarea ref="textarea" class="form-control" rows="1" :placeholder="$t('title.typeYourMessageHere')"
        v-model="formFields.prompt_question" @keydown.enter="handleEnterPress" @input="adjustTextareaHeight"></textarea>
      <b-button variant="primary" class="btn sec-btn small-btn btn-primary rounded border-0" @click="sendMessage"
        :disabled="isSubmit || !formFields.prompt_question">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-arrow-up"
          viewBox="0 0 16 16">
          <path fill-rule="evenodd"
            d="M8 15a.5.5 0 0 0 .5-.5V2.707l3.146 3.147a.5.5 0 0 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 1 0 .708.708L7.5 2.707V14.5a.5.5 0 0 0 .5.5" />
        </svg>
      </b-button>
    </div>
  </div>
</template>

<script>
import { request } from "@/Util/Request";
import Error from "@/Util/Error";
import { mapGetters, mapState } from "vuex";
import { getAuthUser } from "@/Util/auth";

const FORM_STATE = {
  prompt_question: null
};

export default {
  props: {
    selectedChat: Object, // Receive selected chat from parent
  },
  data() {
    return {
      formFields: { ...FORM_STATE },
      formErrors: new Error({}),
      chats: [],
      isSubmit: false,
      newMessage: '',
      isCreateNewChat: false,
      showSuggestedQuestions: false,
      activeCategory: 0,
      searchQuery: '',
      selectedAssistant: 'automatic', // Default selection
      currentStatus: '',
      statusInterval: null,
      isStreaming: false,
      streamingText: '',
      streamingInterval: null,
      currentStep: 0,
      liveEventLog: [],
      eventDisplayQueue: [],
      queueTimer: null,
      displayDelayMs: 1200,
      statusTicker: '',
      // typewriter control
      typingTargets: {},
      typingIntervals: {},
      typingSpeedMs: 25, // Faster typing for better UX
      typingStepChars: 3, // Smaller chunks for smoother effect
      indexNames: {
        rakoon_sap: this.$t('title.rakoonSap'),
        rakoon_salesforse_monday: this.$t('title.rakoonSalesforceMonday'),
        rakoon_office_gsuit: this.$t('title.rakoonOfficeGsuite'),
        rakoon_notion: this.$t('title.rakoonNotion')
      },
      // Organized questions by category
      questionCategories: [
        {
          name: this.$t('title.userOrganization'),
          questions: [
            "How many active users do we have across Office 365 and G-Suite?",
            "Which departments or job titles are most represented in our user base?",
            "Are there users with missing job titles or contact information? How can we complete that?",
            "Which users are marked as is_saved = 1? What does that indicate?",
            "How many users are using non-English preferred languages (e.g., ru-RU, en-GB)?",
            "List all Office 365 users and also show the subject of the most recent email notification.",
            "Who is the most recently created user, and what was the latest Teams chat message received?",
            "Show me all users with their job titles, and summarize the chat messages from the latest email notification.",
            "Which users belong to checker-soft.com, and who are the participants in the last Teams notification email?",
            "Give me the job title of Kate Yarosh and also the subject line of the latest Teams email.",
            "Who has the language setting en-GB, and what message did Milan send in the chat?",
            "List all users created on 2025-02-21 and also tell me when the last email notification was received.",
            "Who is listed as an external guest user, and who mentioned 'Today's Update worked on designing part with Dhaval' in the chat?",
            "Which user is located in UA, and what are the links included in the last Teams notification email?",
            "Show me the full names and job titles of all users, then summarize the most recent email notification in one line.",
            "How many Office 365 users exist, and how many participants were in the latest Teams notification?",
            "Which users don't have a job title, and what was the last email subject they may have received?",
            "Show me users from the checker-soft.com domain and list the chat messages they were part of in the last email.",
            "Who is the oldest user (by createdDateTime), and what was the first message in the most recent chat notification?",
            "List all users with preferred language settings, and show which language was used in the Teams email subject.",
            "Which users were created on the same day the last email was received?",
            "Who has the office location as UA, and what was Krishna's latest chat update?",
            "Which users don't have a manager assigned, and who was the last sender in the most recent chat?",
            "Show me all users with their principal names and also the time the latest Teams notification was delivered.",
            "List the users created after 2025-02-20, and summarize the email preview text.",
            "How many user records have missing office_location or job_title?",
            "Are there duplicate user entries across Office 365 and G-Suite?",
            "How many users have multiple email aliases?",
            "Is the linked_entity field being used? If not, should we leverage it?",
            "Which office locations (e.g., UA, IL, US) have the most users?",
            "How many users prefer a language other than English?",
            "Are there users with phone numbers from specific regions (e.g., +972 for Israel)?"
          ]
        },
        {
          name: this.$t('title.emailCommunication'),
          questions: [
            "How many emails were sent from gingersauce.co regarding 'Brand Book' completions?",
            "What is the volume of automated emails (e.g., OTP, notifications) vs. human-generated emails?",
            "Which internal email threads (e.g., about GCP service accounts) involve the most people?",
            "Are there any recurring external domains receiving emails from us (e.g., @gmail.com, @gingersauce.co)?",
            "How many emails contain multilingual or non-Latin characters (e.g., Arabic, Japanese, Hebrew)?",
            "Show me the external guest users and also summarize the body preview of the latest Teams email.",
            "How many Brand Book completion emails were sent in the last week? Is there a trend?",
            "Which users are most frequently mentioned in email threads (e.g., @Dhaval Kapupara)?",
            "How many emails include links to internal systems like Teams or GCP?",
          ]
        },
        {
          name: this.$t('title.securityCompliance'),
          questions: [
            "How many OTP emails were sent to krutika.patel@checker-solutions.com? Is that normal?",
            "Are there any emails from suspicious or external domains (e.g., jalvin987@gmail.com sending OTPs)?",
            "How many service accounts or automated senders (e.g., no-reply@teams.microsoft.com) are in the system?",
            "Are there any users with outdated or missing updated_at timestamps?",
            "Which user has the job title Programmer, and what subject line did they likely see in the Teams notification?"
          ]
        },
        {
          name: this.$t('title.dataAnalysis'),
          questions: [
            "What patterns can be identified in user creation dates?",
            "How does email traffic vary by day of week and time of day?",
            "What is the correlation between user department and preferred language?",
          ]
        }
      ]
    };
  },
  computed: {
    // Get all questions from all categories
    allQuestions() {
      return this.questionCategories.flatMap(category => category.questions);
    },
    // Progress width for bar (0-100%)
    progressWidth() {
      const stepToPercent = { 0: '0%', 1: '25%', 2: '55%', 3: '80%', 4: '100%' };
      return stepToPercent[this.currentStep] || '0%';
    },
    // Filter questions based on search and category
    filteredQuestions() {
      let questions = [];

      // If search query exists, search across all questions
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        return this.allQuestions.filter(q => q.toLowerCase().includes(query));
      }

      // Otherwise, show questions from selected category
      if (this.questionCategories[this.activeCategory]) {
        questions = this.questionCategories[this.activeCategory].questions;
      }

      return questions;
    }
  },
  mounted() {
    const urlParams = new URLSearchParams(window.location.search);
    const description = urlParams.get("description");

    if (description) {
      this.formFields.prompt_question = description; // Set the input field value
      this.adjustTextareaHeight(); // Adjust textarea height if needed
    }
  },
  components: {
  },
  watch: {
    selectedChat: {
      immediate: true, // Fetch details when component is mounted
      handler(newChat) {
        if (newChat && newChat.id) {
          this.fetchChatDetails(newChat.id);
        } else {
          this.chatDetails = null;
        }
      },
    },
  },
  methods: {
    pushLiveLog(message, kind = 'status', live = false) {
      if (!message) return;
      this.liveEventLog.push({ text: message, kind, live });
      if (this.liveEventLog.length > 4) {
        this.liveEventLog.shift();
      }
    },
    formatFriendlyStatus(statusKey) {
      if (typeof statusKey !== 'string') return '';
      if (statusKey.includes('aiSearching')) {
        return this.$t('title.rakoonaiFindingSources') || 'RakoonAI is finding the relevant data sources for your question.';
      }
      if (statusKey.includes('aiCompiling')) {
        return this.$t('title.rakoonaiCompilingAnswer') || 'RakoonAI is compiling an answer for you.';
      }
      if (statusKey.includes('aiAnalyzing')) {
        return this.$t('title.rakoonaiAnalyzingResults') || 'RakoonAI is analyzing the results.';
      }
      if (statusKey.includes('aiGenerating')) {
        return this.$t('title.rakoonaiComposingAnswer') || 'RakoonAI is composing your answer.';
      }
      if (statusKey.includes('aiFinalizing')) {
        return this.$t('title.rakoonaiFinalizing') || 'Finalizing your answer.';
      }
      if (statusKey.includes('aiThinking')) {
        return this.$t('title.aiThinking') || 'Thinking...';
      }
      return statusKey;
    },
    extractSource(label) {
      if (!label || typeof label !== 'string') return '';
      return label.replace(/^title\.(searchingIn|foundResultsIn)\s*/i, '').trim();
    },
    applyAnimatedReveal(idx) {
      this.$nextTick(() => {
        const el = this.$refs['chatText-' + idx];
        const node = Array.isArray(el) ? el[0] : el;
        if (!node) return;
        node.classList.remove('reveal-play');
        // trigger reflow to restart animation
        // eslint-disable-next-line no-unused-expressions
        void node.offsetWidth;
        node.classList.add('reveal-play');
      });
    },
    startStatusUpdates() {
      const statuses = [
        this.$t('title.aiThinking'),
        this.$t('title.aiSearching'),
        this.$t('title.aiCompiling'),
        this.$t('title.aiAnalyzing'),
        this.$t('title.aiGenerating'),
        this.$t('title.aiFinalizing')
      ];

      let currentIndex = 0;
      this.currentStatus = statuses[currentIndex];

      this.statusInterval = setInterval(() => {
        currentIndex = (currentIndex + 1) % statuses.length;
        this.currentStatus = statuses[currentIndex];
      }, 2000); // Change status every 2 seconds
    },

    stopStatusUpdates() {
      if (this.statusInterval) {
        clearInterval(this.statusInterval);
        this.statusInterval = null;
      }
      this.currentStatus = '';
    },

    startStreamingResponse(fullText) {
      this.isStreaming = true;
      this.streamingText = '';
      let currentIndex = 0;

      this.streamingInterval = setInterval(() => {
        if (currentIndex < fullText.length) {
          this.streamingText += fullText[currentIndex];
          currentIndex++;
          this.$nextTick(() => {
            this.scrollToBottom();
          });
        } else {
          this.stopStreamingResponse();
        }
      }, 30); // Adjust speed: lower = faster, higher = slower
    },

    stopStreamingResponse() {
      if (this.streamingInterval) {
        clearInterval(this.streamingInterval);
        this.streamingInterval = null;
      }
      this.isStreaming = false;
    },

    toggleSuggestedQuestions() {
      this.showSuggestedQuestions = !this.showSuggestedQuestions;
      // Reset search and category when closing/opening
      if (this.showSuggestedQuestions) {
        this.searchQuery = '';
        this.activeCategory = 0;
      }
    },
    selectSuggestedQuestion(question) {
      // Set the selected question as the prompt
      this.formFields.prompt_question = question;

      // Optionally auto-send the question
      this.sendMessage();

      // Hide the suggestions panel after selection
      this.showSuggestedQuestions = false;
    },
    skipMerge(index) {
      // Mark the chat as "merged = false" explicitly so prompt disappears
      this.$set(this.chats[index], "merged", true);

      // Optionally: add a small note for the user
      this.chats[index].response_text +=
        '<div class="text-muted mt-2"><em>(You chose not to merge responses)</em></div>';

      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },
    async mergeAnswers(chat) {
      console.log('merging chat', chat);
      try {
        this.isSubmit = true;
        const response = await request({
          method: 'post',
          url: `/admin/rakoon-chats/merge`,
          data: {
            chat_id: chat.id,
            question: chat.question,
            assistant_type: this.selectedAssistant
          }
        });

        if (response.data.error) {
          this.notifyErrorWithMsg(response.data.error);
        } else if (response.data) {
          this.chats = [...this.chats.slice(0, -1), response.data];
          this.$nextTick(() => {
            this.scrollToBottom();
          })
        } else {
          this.notifyError();
        }

        this.isSubmit = false;
      } catch (error) {
        this.isSubmit = false;
        if (error.request && error.request.status && error.request.status === 422) {
          this.formErrors = new Error(JSON.parse(error.request.responseText).errors);
          return false;
        }
      }

    },
    async createNewChat() {
      this.chats = [];
      this.isCreateNewChat = true;
    },
    async removeError(key) {
      if (typeof key === `object`) {
        for (let i = 0; i < key.length; i++) {
          this.formErrors.remove(key[i]);
        }
      } else {
        this.formErrors.remove(key);
      }
    },
    async fetchChatDetails(id = null) {
      try {
        const response = await request({
          method: 'get',
          url: `/admin/rakoon-chats?id=${id}`,
        });

        this.chats = response.data;
        this.$nextTick(() => {
          this.scrollToBottom();
        })

      } catch (error) {
        // Handle error
        // Handle error
      }
    },
    async sendMessage() {
      try {
        this.isSubmit = true;
        this.currentStep = 0; // Reset progress steps
        this.stopStatusUpdates(); // Stop any existing status updates
        this.liveEventLog = [];
        this.eventDisplayQueue = [];
        if (this.queueTimer) { clearTimeout(this.queueTimer); this.queueTimer = null; }

        let promptQuestion = this.formFields.prompt_question;
        this.$emit('updateLatestChatHistory', promptQuestion);
        
        // Add user message to chat
        this.chats.push({
          question: promptQuestion,
          response_text: "",
          isStreaming: true,
          debugLog: []
        });

        // Show immediate friendly status while backend initializes
        this.enqueueDisplay(this.formatFriendlyStatus('title.aiThinking'), 1);
        
        this.formFields.prompt_question = '';
        this.$nextTick(() => {
          this.scrollToBottom();
        });

        // Use Server-Sent Events for streaming
        await this.streamMessage(promptQuestion);

        this.isSubmit = false;
        this.isCreateNewChat = false;
        this.$emit('getChatHistory');
      } catch (error) {
        this.isSubmit = false;
        this.stopStatusUpdates();
        
        // Show error in the chat
        const lastChatIndex = this.chats.length - 1;
        if (lastChatIndex >= 0) {
          this.chats[lastChatIndex].response_text = `<div class="text-danger">Error: ${error.message || 'Something went wrong'}</div>`;
          this.chats[lastChatIndex].isStreaming = false;
        }
        
        if (error.request && error.request.status && error.request.status === 422) {
          this.formErrors = new Error(JSON.parse(error.request.responseText).errors);
          return false;
        }
      }
    },

    async streamMessage(promptQuestion) {
      return new Promise((resolve, reject) => {
        // Skip token for testing since auth is disabled
        const authUser = getAuthUser && typeof getAuthUser === 'function' ? getAuthUser() : {};
        console.log('Auth user for token:', authUser);
        const bearerToken = authUser && authUser.token ? authUser.token : null;

        const params = new URLSearchParams({
          prompt_question: promptQuestion,
          assistant_type: this.selectedAssistant,
          // Pass token via query string because EventSource cannot set headers
          ...(bearerToken ? { token: bearerToken } : {}),
          user_id : authUser && authUser.id ? authUser.id : null
        });
        const streamUrl = `${process.env.VUE_APP_BASE_API_URL}/api/rakoon-chats/stream?${params.toString()}`;
        
        const eventSource = new EventSource(streamUrl);
        console.log('EventSource created for URL:', eventSource);
        
        let currentChatIndex = this.chats.length - 1;
        let isResponseStarted = false;

        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            // Append raw debug line per user request
            const rawLine = `data: ${event.data}`;
            this.liveEventLog.push(rawLine);
            
            switch (data.type) {
              case 'status':
                console.log('Status update:', data.data);
                // queue friendly display to avoid simultaneous bursts
                this.enqueueDisplay(this.formatFriendlyStatus(data.data),
                  data.data.includes('aiSearching') ? 1 :
                  data.data.includes('aiCompiling') || data.data.includes('aiAnalyzing') ? 2 :
                  data.data.includes('aiGenerating') ? 3 : null);
                break;
                
              case 'search_status':
                console.log('Search status:', data.data);
                // using raw event log instead of activity cards
                // Reflect search phase in progress UI
                this.enqueueDisplay(`${this.$t('title.rakoonaiSearchingIn') || 'Searching in'} ${this.extractSource(data.data)}`, 1);
                this.pushLiveLog(`${this.$t('title.rakoonaiSearchingIn') || 'Searching in'} ${this.extractSource(data.data)}...`, 'search', true);
                break;
                
              case 'search_complete':
                console.log('Search complete:', data.data);
                // using raw event log instead of activity cards
                // Move to compiling/analyzing after first search completes
                this.enqueueDisplay(`${this.$t('title.rakoonaiFoundIn') || 'Found results in'} ${this.extractSource(data.data)}`, 2);
                this.pushLiveLog(`${this.$t('title.rakoonaiFoundIn') || 'Found results in'} ${this.extractSource(data.data)}`, 'success');
                break;
                
              case 'route':
                console.log('Route decided:', data.data);
                this.enqueueDisplay(`${this.$t('title.rakoonaiFoundSources') || 'Selected sources'}: ${data.data}`, 1);
                this.pushLiveLog(`${this.$t('title.rakoonaiFoundSources') || 'Selected sources'}: ${data.data}`, 'status');
                break;

              case 'response_start':
                console.log('Response started');
                isResponseStarted = true;
                this.chats[currentChatIndex].response_text = '';
                // Generating phase when streaming begins
                this.enqueueDisplay(this.$t('title.rakoonaiComposingAnswer') || this.$t('title.aiGenerating'), 3);
                this.pushLiveLog(this.$t('title.rakoonaiComposingAnswer') || this.$t('title.aiGenerating'), 'compose', true);
                break;
                
              case 'response_chunk':
                if (isResponseStarted) {
                  this.chats[currentChatIndex].isStreaming = true;
                  // target text for typewriter
                  this.$set(this.typingTargets, currentChatIndex, data.data);
                  if (!this.typingIntervals[currentChatIndex]) {
                    this.typingIntervals[currentChatIndex] = setInterval(() => {
                      const target = this.typingTargets[currentChatIndex] || '';
                      const current = this.chats[currentChatIndex].response_text || '';
                      if (current.length >= target.length) {
                        return; // wait for next target growth
                      }
                      const nextLen = Math.min(current.length + this.typingStepChars, target.length);
                      this.chats[currentChatIndex].response_text = target.substring(0, nextLen);
                      this.$nextTick(() => {
                        // Auto-scroll to keep the typing cursor visible
                        this.scrollToBottom();
                        this.applyAnimatedReveal(currentChatIndex);
                      });
                    }, this.typingSpeedMs);
                  }
                }
                break;
                
              case 'response_complete':
                console.log('Response complete:', data.data);
                this.chats[currentChatIndex].response_text = data.data;
                this.chats[currentChatIndex].isStreaming = false;
                // flush any remaining target and clear interval
                if (this.typingIntervals[currentChatIndex]) {
                  clearInterval(this.typingIntervals[currentChatIndex]);
                  this.$delete(this.typingIntervals, currentChatIndex);
                }
                this.$delete(this.typingTargets, currentChatIndex);
                if (data.chat) {
                  // Update with final chat data
                  Object.assign(this.chats[currentChatIndex], data.chat);
                }
                this.$nextTick(() => {
                  this.scrollToBottom();
                  this.applyAnimatedReveal(currentChatIndex);
                });
                // Finalizing step when complete
                this.enqueueDisplay(this.$t('title.rakoonaiFinalizing') || this.$t('title.aiFinalizing'), 4);
                eventSource.close();
                resolve();
                break;
                
              case 'test':
                console.log('Test message:', data.data);
                // For test stream, just show the message
                if (!this.chats[currentChatIndex].response_text) {
                  this.chats[currentChatIndex].response_text = '';
                }
                this.chats[currentChatIndex].response_text += data.data + '<br>';
                // Treat test content as generating
                this.currentStep = Math.max(this.currentStep, 3);
                this.liveEventLog.push(`data: ${event.data}`);
                this.$nextTick(() => {
                  this.scrollToBottom();
                });
                break;
                
              case 'complete':
                console.log('Test completed');
                this.chats[currentChatIndex].isStreaming = false;
                this.currentStep = 4;
                this.currentStatus = this.$t('title.aiFinalizing');
                eventSource.close();
                resolve();
                break;
                
              case 'error':
                console.log('Error received:', data.data);
                this.chats[currentChatIndex].response_text = `<div class="text-danger">Error: ${data.data}</div>`;
                this.chats[currentChatIndex].isStreaming = false;
                if (this.typingIntervals[currentChatIndex]) {
                  clearInterval(this.typingIntervals[currentChatIndex]);
                  this.$delete(this.typingIntervals, currentChatIndex);
                }
                eventSource.close();
                reject(new Error(data.data));
                break;
                
              default:
                console.log('Unknown message type:', data.type, data.data);
            }
          } catch (error) {
            console.error('Error parsing SSE data:', error, 'Raw data:', event.data);
          }
        };

        eventSource.onerror = (error) => {
          console.error('EventSource error:', error);
          
          // Try fallback to regular API if streaming fails
          this.fallbackToRegularAPI(promptQuestion, currentChatIndex)
            .then(() => {
              eventSource.close();
              resolve();
            })
            .catch((fallbackError) => {
              this.chats[currentChatIndex].response_text = `<div class="text-danger">Connection error occurred. Please try again.</div>`;
              this.chats[currentChatIndex].isStreaming = false;
              eventSource.close();
              reject(fallbackError);
            });
        };

        // EventSource will handle the request automatically
      });
    },

    async sendStreamRequest(promptQuestion) {
      try {
        const response = await fetch('/api/rakoon-chats/stream', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          },
          body: JSON.stringify({
            prompt_question: promptQuestion,
            assistant_type: this.selectedAssistant
          })
        });
        console.log('Stream request sent, response status:', response.status);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } catch (error) {
        console.error('Stream request error:', error);
        throw error;
      }
    },

    updateCurrentStatus(status) {
      const localized = (typeof status === 'string' && status.startsWith('title.')) ? this.$t(status) : status;
      this.currentStatus = localized;
      
      // Update progress steps based on status - support both Hebrew and English
      if (localized.includes('חושב') || localized.includes('Thinking') || localized.includes(this.$t('title.aiThinking'))) {
        this.currentStep = 1;
      } else if (localized.includes('מחפש') || localized.includes('Searching') || localized.includes(this.$t('title.aiSearching'))) {
        this.currentStep = 1;
      } else if (localized.includes('מקבץ') || localized.includes('Gathering') || localized.includes(this.$t('title.aiCompiling'))) {
        this.currentStep = 2;
      } else if (localized.includes('מנתח') || localized.includes('Analyzing') || localized.includes(this.$t('title.aiAnalyzing'))) {
        this.currentStep = 2;
      } else if (localized.includes('יוצר') || localized.includes('Generating') || localized.includes(this.$t('title.aiGenerating'))) {
        this.currentStep = 3;
      } else if (localized.includes('Complete') || localized.includes('Finished') || localized.includes(this.$t('title.aiFinalizing'))) {
        this.currentStep = 4;
      }
    },
    enqueueDisplay(message, step = null) {
      this.eventDisplayQueue.push({ message, step });
      if (!this.queueTimer) {
        this.processDisplayQueue();
      }
    },
    processDisplayQueue() {
      if (this.eventDisplayQueue.length === 0) {
        this.queueTimer = null;
        return;
      }
      const item = this.eventDisplayQueue.shift();
      if (item.step !== null && item.step !== undefined) {
        this.currentStep = Math.max(this.currentStep, item.step);
      }
      this.currentStatus = item.message;
      this.statusTicker = item.message;
      this.pushLiveLog(item.message, 'status', true);
      // If we are in composing phase, slow down slightly for nicer UX
      let delay = this.displayDelayMs;
      if (item.step === 1) delay += 200; // searching
      if (item.step === 2) delay += 300; // compiling/analyzing
      if (item.step === 3) delay += 700; // composing
      this.queueTimer = setTimeout(() => this.processDisplayQueue(), delay);
    },
    // search activity cards removed; using raw debug log instead

    async fallbackToRegularAPI(promptQuestion, chatIndex) {
      try {
        const response = await request({
          method: 'post',
          url: `/admin/rakoon-chats`,
          data: {
            prompt_question: promptQuestion,
            assistant_type: this.selectedAssistant
          }
        });

        if (response.data.error) {
          this.chats[chatIndex].response_text = `<div class="text-danger">Error: ${response.data.error}</div>`;
        } else if (response.data) {
          this.chats[chatIndex] = { ...this.chats[chatIndex], ...response.data };
        } else {
          this.chats[chatIndex].response_text = `<div class="text-danger">No response received</div>`;
        }
        
        this.chats[chatIndex].isStreaming = false;
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      } catch (error) {
        this.chats[chatIndex].response_text = `<div class="text-danger">Fallback failed: ${error.message}</div>`;
        this.chats[chatIndex].isStreaming = false;
        throw error;
      }
    },

    handleEnterPress(event) {
      if (event.shiftKey) {
        // If Shift is pressed, add a new line
        event.preventDefault();
        this.formFields.prompt_question += '\n';
        this.$nextTick(() => this.adjustTextareaHeight());
      } else {
        // Otherwise, send the message
        event.preventDefault();
        this.sendMessage();
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const chatContainer = this.$refs.chatContainer;
        if (chatContainer) {
          // Smooth scroll to bottom to follow the typing
          chatContainer.scrollTo({
            top: chatContainer.scrollHeight,
            behavior: 'smooth'
          });
        }
      });
    },
    adjustTextareaHeight() {
      this.$nextTick(() => {
        const textarea = this.$refs.textarea;
        if (textarea) {
          textarea.style.height = "auto"; // Reset height
          textarea.style.height = `${textarea.scrollHeight}px`; // Set new height
        }
      });
    },
    resetTextareaHeight() {
      const textarea = this.$refs.textarea;
      if (textarea) {
        textarea.style.height = "auto"; // Reset to default after sending a message
      }
    },
    formatTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    },
  },
};
</script>

<style scoped>
.suggested-questions-container {
  position: relative;
}

.assistant-dropdown-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.dropdown-label {
  font-size: 0.9rem;
  font-weight: 500;
  color: #495057;
  margin: 0;
  white-space: nowrap;
}

.assistant-dropdown {
  max-width: 200px;
  font-size: 0.85rem;
  padding: 4px 8px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background-color: #fff;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.assistant-dropdown:focus {
  border-color: #007bff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.suggested-questions-panel {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  max-height: 400px;
  display: flex;
  flex-direction: column;
}

.question-categories {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 10px;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 8px;
}

.category-tab {
  background-color: #e9ecef;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.category-tab:hover,
.category-tab.active {
  background-color: #007bff;
  color: white;
  border-color: #007bff;
}

.question-list-container {
  overflow-y: auto;
  flex-grow: 1;
  margin-bottom: 10px;
}

.question-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.suggested-question-btn {
  background-color: #e9ecef;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  white-space: normal;
  word-break: break-word;
}

.suggested-question-btn:hover {
  background-color: #007bff;
  color: white;
  border-color: #007bff;
}

.search-container {
  margin-top: auto;
}

.merge-question {
  background: #f0f8ff;
  /* light blue */
  border: 2px solid #007bff;
  border-radius: 8px;
  font-size: 14px;
}

.suggested-btn {
  background-color: #007bff;
  color: #fff;
  border: none;
  border-radius: 20px;
  /* pill-shaped */
  padding: 6px 16px;
  font-size: 0.85rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.suggested-btn:hover {
  background-color: #0056b3;
  transform: translateY(-1px);
}

.suggested-btn:active {
  background-color: #004494;
  transform: translateY(0);
}

.suggested-btn i {
  font-size: 0.9rem;
}

.ai-status-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 15px;
  margin: 10px 0;
}

.status-card { display: flex; justify-content: space-between; align-items: center; padding: 10px 14px; border: 1px solid #e9ecef; border-radius: 12px; background: #fff; box-shadow: 0 2px 6px rgba(0,0,0,0.05); }
.status-left { display: flex; align-items: center; gap: 10px; }
.status-spinner { width: 14px; height: 14px; border: 2px solid #cfe2ff; border-top-color: #1e88e5; border-radius: 50%; animation: spin 1s linear infinite; }
.status-text { font-size: 0.95rem; color: #374151; font-weight: 500; }
.status-right { display: flex; align-items: center; gap: 6px; }
.status-badge { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 50%; background: #e9ecef; color: #6c757d; }
.status-badge.on { background: #e6f4ea; color: #34a853; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Progress Steps */
.ai-progress-info {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #dee2e6;
}

.ai-live-log { margin-bottom: 10px; background: #fafbff; border: 1px solid #eef1f7; border-radius: 10px; padding: 6px 8px; max-height: 84px; overflow-y: auto; }
.ai-live-log-line { font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace; font-size: 11px; color: #5f6368; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* New progress bar */
.ai-loader { margin-top: 10px; }
.loader-bar { position: relative; height: 6px; border-radius: 6px; background: #eef1f7; overflow: hidden; }
.loader-shimmer { position: absolute; left: -30%; top: 0; bottom: 0; width: 30%; background: linear-gradient(90deg, rgba(66,165,245,0.1), rgba(66,165,245,0.6), rgba(66,165,245,0.1)); animation: shimmer 1.2s infinite; }
@keyframes shimmer { 0% { left: -30%; } 100% { left: 100%; } }

/* Inline live log near chat bubble */
.inline-live-log { background: #f5f9ff; border: 1px solid #e3edff; color: #2b4c7e; border-radius: 8px; padding: 6px 8px; font-size: 12px; margin-bottom: 8px; }
.status-ticker { display: inline-flex; align-items: center; gap: 8px; background: #eef6ff; border: 1px solid #d9e9ff; color: #245aa5; padding: 6px 10px; border-radius: 999px; box-shadow: 0 1px 2px rgba(0,0,0,0.04); }
.status-ticker .fa { font-size: 13px; }
.status-ticker .text { font-weight: 500; }
.inline-live-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 4px; }
.inline-live-item { display: flex; align-items: center; gap: 8px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.inline-live-item .fa { font-size: 12px; }
.inline-live-item.search .fa { color: #1e88e5; }
.inline-live-item.success .fa { color: #2e7d32; }
.inline-live-item.analyze .fa, .inline-live-item.compile .fa { color: #7b1fa2; }
.inline-live-item.compose .fa { color: #1976d2; }

@keyframes pulse {
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0.7;
  }

  100% {
    opacity: 1;
  }
}

/* Search Activities Styles */
.search-activities {
  display: none;
}

.search-activities-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #dee2e6;
}

.search-activities-title {
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
}

.search-activities-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.search-activity {
  margin-bottom: 0;
}

.search-activity-item { display: none; }

.search-activity-item::before { display: none; }

.search-activity-item.searching {
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
  color: #1976d2;
  border: 1px solid #bbdefb;
  animation: pulse-glow 2s infinite;
}

.search-activity-item.searching::before {
  background-color: #1976d2;
  animation: pulse-glow 2s infinite;
}

.search-activity-item.completed {
  background: #ffffff;
  color: #495057;
  border: 1px solid #e9ecef;
}

.search-activity-item.completed::before {
  background-color: #9aa0a6; /* neutral */
}

.search-activity-item.error {
  background: #fff7f7;
  color: #c62828;
  border: 1px solid #f3d6d6;
}

.search-activity-item.error::before {
  background-color: #e57373;
}

.search-activity-icon {
  margin-right: 10px;
  font-size: 1rem;
  min-width: 20px;
  color: #6c757d;
}

.search-activity-item.completed .search-activity-icon i {
  color: #6c757d;
}

.search-activity-content {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.search-message {
  font-weight: 500;
}

.search-activity-time {
  font-size: 0.75rem;
  color: #6c757d;
  opacity: 0.8;
}

.search-spinner {
  margin-left: 10px;
}

@keyframes pulse-glow {
  0%, 100% {
    box-shadow: 0 0 5px rgba(25, 118, 210, 0.3);
  }
  50% {
    box-shadow: 0 0 15px rgba(25, 118, 210, 0.6);
  }
}

/* Typing Indicator */
.typing-indicator {
  display: flex;
  align-items: center;
  padding: 10px 0;
}

/* Event log */
.event-log { background: #f8f9fb; border: 1px solid #e9ecef; border-radius: 10px; padding: 10px; }
.event-log-header { font-weight: 600; color: #495057; margin-bottom: 6px; }
.event-log-list { font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace; font-size: 12px; color: #5f6368; max-height: 160px; overflow-y: auto; line-height: 1.5; }
.event-log-line { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; padding: 2px 0; }

.typing-dots {
  display: flex;
  align-items: center;
  gap: 4px;
}

.typing-dots span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #007bff;
  animation: typing 1.4s infinite ease-in-out;
}

.typing-dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.typing-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes typing {
  0%, 80%, 100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Streaming text animation */
.chat-text {
  position: relative;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 12px;
  padding: 15px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  line-height: 1.6;
  font-size: 0.95rem;
}

/* subtle reveal animation for new content */
.animated-reveal { mask-image: linear-gradient(90deg, transparent 0, #000 0); }
.animated-reveal.reveal-play { animation: revealText 800ms ease-out; }
@keyframes revealText {
  0% { opacity: 0.3; transform: translateY(4px); }
  100% { opacity: 1; transform: translateY(0); }
}

.chat-text.streaming::after {
  content: '|';
  animation: blink 1s infinite;
  color: #007bff;
  font-weight: bold;
  margin-left: 2px;
}

.chat-text h1, .chat-text h2, .chat-text h3, .chat-text h4, .chat-text h5, .chat-text h6 {
  color: #2c3e50;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}

.chat-text p {
  margin-bottom: 0.8rem;
}

.chat-text ul, .chat-text ol {
  margin-bottom: 0.8rem;
  padding-left: 1.5rem;
}

.chat-text li {
  margin-bottom: 0.3rem;
}

.chat-text code {
  background-color: #f1f3f4;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 0.9em;
}

.chat-text pre {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 12px;
  overflow-x: auto;
  margin: 1rem 0;
}

.chat-text blockquote {
  border-left: 4px solid #007bff;
  padding-left: 1rem;
  margin: 1rem 0;
  color: #6c757d;
  font-style: italic;
}

@keyframes blink {
  0%, 50% {
    opacity: 1;
  }
  51%, 100% {
    opacity: 0;
  }
}

@keyframes blink-strong {
  0% { box-shadow: 0 0 0 rgba(0, 123, 255, 0); opacity: 1; }
  50% { box-shadow: 0 0 12px rgba(0, 123, 255, 0.6); opacity: 0.85; }
  100% { box-shadow: 0 0 0 rgba(0, 123, 255, 0); opacity: 1; }
}
</style>
